/**
 * Campaign console – the overview at `/campaigns`.
 *
 * Every campaign the seats run except B2G, which has its own Outreach entry
 * and keeps its waves, threads and the outreach mailboxes' kill switch on its
 * page: what waits for the owner (P1 items, bundles, threads where it is his
 * turn), and one row per campaign with its funnel. Nothing here creates a
 * campaign or enrolls anyone – a campaign joins the console with a `console`
 * config (campaign_upsert). Data: `api/console.ts :: useConsoleOverview`.
 */
import { useState } from "react";
import { Link } from "react-router-dom";

import type { NeedsYouKind } from "../api/console";
import { useConsoleOverview } from "../api/console";
import { Age } from "../components/Age";
import { FunnelBar } from "../components/console/parts";
import { PageHeader } from "../components/PageHeader";
import { Pill, type PillTone } from "../components/Pill";
import { PriorityBadge } from "../components/PriorityBadge";
import { QueryBoundary } from "../components/QueryBoundary";
import { StatTile } from "../components/StatTile";
import { ageSeconds, B2G_CAMPAIGN_SLUG, deliveryLabel, replyRate } from "../lib/console";
import { berlinDay, whenLabel } from "../lib/sendPlan";

/** B2G has its own Outreach entry, so the overview leaves it out entirely. */
const OWN_PAGE = [B2G_CAMPAIGN_SLUG];

const KIND_PILL: Record<NeedsYouKind, { label: string; tone: PillTone }> = {
  item: { label: "P1 item", tone: "alert" },
  bundle: { label: "bundle", tone: "signal" },
  wave: { label: "wave", tone: "signal" },
  thread: { label: "your turn", tone: "alert" },
};

export function CampaignsPage() {
  const overview = useConsoleOverview(OWN_PAGE);
  const [nowMs] = useState(() => Date.now());
  const today = berlinDay(new Date(nowMs));

  return (
    <div className="page">
      <PageHeader
        title="Campaigns"
        description="Every campaign the seats run, B2G aside – it has its own entry. You decide bundles and your-turn threads, watch what goes out and what comes back, and step in – nothing here creates or enrolls."
      />

      <QueryBoundary query={overview}>
        {(data) => {
          const { stats } = data;
          const rr = stats.reply_rate_30d;
          return (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <StatTile
                  label="Bundles for you"
                  figure={String(stats.open_bundles)}
                  deltaTone={stats.open_bundles ? "warn" : null}
                  delta={stats.open_bundles ? "decide below" : undefined}
                />
                <StatTile
                  label="Your turn"
                  figure={String(stats.your_turn)}
                  deltaTone={stats.your_turn ? "warn" : null}
                  delta={stats.your_turn ? "sign, call or decide" : undefined}
                />
                <StatTile
                  label="Reply rate 30 d"
                  figure={replyRate(rr.replied, rr.contacted)}
                  delta={
                    rr.contacted
                      ? `${rr.replied} of ${rr.contacted} threads – automatic replies not counted`
                      : "no thread started in 30 days"
                  }
                />
                <StatTile label="Active campaigns" figure={String(stats.active_campaigns)} />
              </div>

              <div className="panel panel--flush mb-6">
                <div className="panel-header">
                  <h2>Needs you</h2>
                  <span className="text-xs text-ink-low">P1 items first, then bundles, then threads where it is your turn</span>
                </div>
                {data.needs_you.length ? (
                  <table className="table">
                    <tbody>
                      {data.needs_you.map((row) => (
                        <tr key={`${row.kind}:${row.href}:${row.title}`}>
                          <td className="w-28">
                            <Pill value={KIND_PILL[row.kind].label} tone={KIND_PILL[row.kind].tone} />
                          </td>
                          <td>
                            <Link to={row.href} className="link">
                              {row.title}
                            </Link>
                            <div className="text-xs text-ink-low">
                              {row.campaign_name ?? row.campaign_slug ?? "–"}
                              {row.seat ? (
                                <>
                                  {" · "}
                                  <span className="font-mono">{row.seat}</span>
                                </>
                              ) : null}
                            </div>
                          </td>
                          <td className="w-16">{row.priority ? <PriorityBadge priority={row.priority} /> : null}</td>
                          <td className="w-20 text-right">
                            {row.since ? <Age seconds={ageSeconds(row.since, nowMs)} /> : null}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="px-6 py-4 text-sm text-ink-low">Nothing waits for you.</div>
                )}
              </div>

              <div className="panel panel--flush">
                <div className="panel-header">
                  <h2>Campaigns</h2>
                  <span className="text-xs text-ink-low">planned · approved · contacted · in dialogue · closed</span>
                </div>
                {data.campaigns.length ? (
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Campaign</th>
                        <th>Delivery</th>
                        <th>Funnel</th>
                        <th className="num">Your turn</th>
                        <th>Next send</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.campaigns.map((c) => (
                        <tr key={c.slug}>
                          <td>
                            <Link to={`/campaigns/${c.slug}`} className="font-heading font-semibold text-ink hover:text-accent">
                              {c.name}
                            </Link>
                            <div className="text-xs text-ink-low">
                              <span className="font-mono">{c.slug}</span> · {c.seat_name ?? c.seat_slug} · counts {c.unit}
                            </div>
                          </td>
                          <td className="text-sm">
                            {deliveryLabel(c.delivery)}
                            {c.mailbox ? <div className="text-xs text-ink-low font-mono">{c.mailbox}</div> : null}
                          </td>
                          <td>
                            <FunnelBar funnel={c.funnel} />
                          </td>
                          <td className="num">{c.your_turn || "–"}</td>
                          <td className="text-sm whitespace-nowrap">
                            {c.next_send
                              ? `≈ ${whenLabel(c.next_send, today)}`
                              : c.open_bundles
                                ? `${c.open_bundles} bundle${c.open_bundles === 1 ? "" : "s"} waiting`
                                : "–"}
                          </td>
                          <td>
                            <Pill value={c.status} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="empty-state">
                    <div className="title">No campaign on the console yet</div>
                    <p className="hint">
                      A campaign joins it with a <span className="font-mono">console</span> config via campaign_upsert.
                    </p>
                  </div>
                )}
              </div>
            </>
          );
        }}
      </QueryBoundary>
    </div>
  );
}
